import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import makeStyles from '@mui/styles/makeStyles';
import React, { useState } from 'react';
import { AuthContext, AuthData } from '../../context/auth';
import { ConfigContext, ConfigData } from '../../context/config';
import { getDateErrors, getReadableName } from '../../util/util';
import FieldLabel from '../fields/FieldLabel';
import DefaultDatePicker, {
	DefaultDatePickerValue,
} from '../form/DefaultDatePicker';
import LargeCenterSpinner from '../LargeCenterSpinner';
import ReactSelect from '../policy/fields/ReactSelect';
import ReportTable from './ReportTable';

const useStyles = makeStyles((theme) => ({
	root: {
		flexGrow: 1,
		marginTop: '20px',
	},
	formControl: {
		minWidth: 300,
		maxWidth: 500,
	},
}));

interface Props {
	configData: ConfigData;
	authData: AuthData;
	reportResults: any;
	loadingResults: boolean;
	onUpdate: (reqObj: any, hasErrors: boolean) => void;
}

const OnRiskReport = (props: Props) => {
	const classes = useStyles();
	const { configData, onUpdate, reportResults, loadingResults, authData } =
		props;
	const { productConfigs } = configData;
	const [reportTimestamp, setReportStartTimestamp] =
		useState<DefaultDatePickerValue>(null);
	const [products, setProducts] = useState([]);
	const timestampErrors = getDateErrors(
		reportTimestamp,
		null,
		null,
		true,
		authData,
	);

	const latestVersion = productConfigs.find(
		(p) => p.name === 'global',
	).configVersion;
	const latestProducts = productConfigs.filter(
		(p) => p.configVersion === latestVersion,
	);
	const productNames = latestProducts
		.map((p) => p.name)
		.filter((name) => name !== 'global');
	const selectedProductNames = products.map((p) => p.value);

	const updateStartDate = (date: DefaultDatePickerValue) => {
		const validDate = date && !isNaN(date.getTime());
		setReportStartTimestamp(date);
		const req = {
			reportTimestamp: validDate ? `${(date as Date).getTime()}` : null,
			productNames: selectedProductNames,
		};
		// get updated errors to send back to reportsView
		const hasErrors = getDateErrors(date, null, null, true, authData);
		onUpdate(req, !hasErrors.isValid);
	};

	const updateProducts = (opt: any) => {
		setProducts(opt);
		const productNamesArr = opt.map((p: any) => p.value);
		const req = {
			reportTimestamp: reportTimestamp ? `${reportTimestamp.getTime()}` : null,
			productNames: productNamesArr,
		};
		onUpdate(req, !timestampErrors.isValid);
	};

	const productNameOptions = productNames.map((productName) => ({
		value: productName,
		label: getReadableName(productName),
	}));

	const renderReportDetails = () => {
		return (
			<Grid container className={classes.root}>
				<Grid item xs={12}>
					<Grid container justifyContent='center'>
						<DefaultDatePicker
							disabled={!!reportResults || loadingResults}
							id='asOfDate'
							error={!timestampErrors.isValid}
							errorMessage={
								!timestampErrors.isValid ? timestampErrors.message : null
							}
							label='As Of Date'
							showCalendar
							required
							style={{ marginRight: '10px' }}
							value={reportTimestamp}
							onChange={(date) => updateStartDate(date)}
						/>
						<FormControl className={classes.formControl}>
							<FieldLabel
								title='Select Products'
								id='all-policies-product-select'
								required
							/>
							<ReactSelect
								options={productNameOptions}
								value={products}
								onChange={updateProducts}
								isDisabled={!!reportResults || loadingResults}
								isMulti
							/>
						</FormControl>
					</Grid>
				</Grid>
			</Grid>
		);
	};

	return (
		<>
			{renderReportDetails()}
			{loadingResults && <LargeCenterSpinner />}
			{reportResults && <ReportTable results={reportResults} />}
		</>
	);
};

function withContext(Component: any) {
	return (props: Partial<Props>) => (
		<AuthContext.Consumer>
			{(authData) => (
				<ConfigContext.Consumer>
					{(configData) => (
						<Component {...props} configData={configData} authData={authData} />
					)}
				</ConfigContext.Consumer>
			)}
		</AuthContext.Consumer>
	);
}

export default withContext(OnRiskReport);
